import React, { useState, useEffect } from 'react';
import { Card, Descriptions, Tag, Checkbox, Button, Tooltip, Spin, Select } from 'antd';
import { FileText } from 'lucide-react';
import { safeJSONParse } from '@/utils/helper';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { updateIntakeResponse } from '@/api/logs';
import { NotesModal } from './NotesModal';
import { EditOutlined } from '@ant-design/icons';
import { useAppDispatch } from '@/store/hooks/useAppDispatch';
import { openModal, closeModal } from '@/store/modal/slice';
import { MODAL_IDS } from 'src/enums/EModal';
import { TicketStatus } from '@/pages/ReceptionistDashboard/FormResponses/types';

const { Option } = Select;

interface IntakeResponseTabProps {
	intakeResponses: string;
	intakeMetadata: string;
	agentId: string;
	callSid: string;
	isNewStatusSystemEnabled: boolean;
}

const IntakeResponseTab: React.FC<IntakeResponseTabProps> = ({
	intakeResponses,
	intakeMetadata,
	agentId,
	callSid,
	isNewStatusSystemEnabled,
}) => {
	const [parsedData, setParsedData] = React.useState<any>(null);
	const [error, setError] = React.useState<string | null>(null);
	const queryClient = useQueryClient();
	const [metadata, setMetadata] = useState<any>(null);
	const [currentStatus, setCurrentStatus] = useState<TicketStatus>('OPEN');
	const dispatch = useAppDispatch();

	useEffect(() => {
		if (metadata) {
			if (metadata.status && ['OPEN', 'IN_PROGRESS', 'COMPLETED'].includes(metadata.status)) {
				setCurrentStatus(metadata.status);
			} else if (metadata.completed_at) {
				setCurrentStatus('COMPLETED');
			} else {
				setCurrentStatus('OPEN');
			}
		}
	}, [metadata]);

	const updateResponseMutation = useMutation({
		mutationFn: updateIntakeResponse,
		onMutate: async (newData) => {
			await queryClient.cancelQueries({ queryKey: ['callLogs'] });
			const previousMetadata = metadata;
			setMetadata((currentMetadata: any) => ({
				...currentMetadata,
				completed_at: newData.status === 'COMPLETED' ? new Date().toISOString() : null,
				in_progress_at:
					newData.status === 'IN_PROGRESS'
						? new Date().toISOString()
						: currentMetadata.in_progress_at,
				...(newData.status === 'OPEN' && { completed_at: null, in_progress_at: null }),
				status: newData.status || (newData.is_completed ? 'COMPLETED' : 'OPEN'),
				note: newData.note,
			}));
			return { previousMetadata };
		},

		onError: (_err, _variables, context) => {
			if (context?.previousMetadata) {
				setMetadata(context.previousMetadata);
			}
		},

		onSettled: () => {
			queryClient.invalidateQueries({ queryKey: ['callLogs'] });
		},
	});

	const handleCompletionChange = (checked: boolean) => {
		updateResponseMutation.mutate({
			agentId,
			responseId: `${callSid}_0`,
			is_completed: checked,
			note: metadata?.note || '',
		});
	};

	const handleStatusChange = (newStatus: TicketStatus) => {
		updateResponseMutation.mutate({
			agentId,
			responseId: `${callSid}_0`,
			status: newStatus,
			note: metadata?.note || '',
		});
	};

	const handleOpenNoteModal = () => {
		const onSave = (note: string): Promise<void> => {
			return new Promise((resolve, reject) => {
				updateResponseMutation.mutate(
					{
						agentId,
						responseId: `${callSid}_0`,
						status: isNewStatusSystemEnabled
							? currentStatus
							: metadata?.completed_at
								? 'COMPLETED'
								: 'OPEN',
						note,
					},
					{
						onSuccess: () => {
							dispatch(closeModal());
							resolve();
						},
						onError: (err) => {
							reject(err);
						},
					},
				);
			});
		};

		dispatch(
			openModal({
				modalId: MODAL_IDS.NOTES_MODAL,
				data: {
					title: 'Internal Note',
					initialNote: metadata?.note || '',
					onSave,
				},
			}),
		);
	};

	React.useEffect(() => {
		try {
			const parsed = safeJSONParse(intakeResponses, []);
			setParsedData(parsed);
			setError(null);
		} catch (e) {
			setError('Failed to parse intake response data');
			console.error('Error parsing intake responses:', e);
			setParsedData(null);
		}
	}, [intakeResponses]);

	const renderValue = (value: any) => {
		if (typeof value === 'boolean') {
			return <Tag color={value ? 'success' : 'error'}>{value ? 'Yes' : 'No'}</Tag>;
		}
		if (typeof value === 'object' && value !== null) {
			return <pre className="text-xs bg-gray-50 p-2 rounded">{JSON.stringify(value, null, 2)}</pre>;
		}
		if (Array.isArray(value)) {
			return (
				<div className="space-y-1">
					{value.map((item, index) => (
						<Tag key={index} className="mb-1">
							{String(item)}
						</Tag>
					))}
				</div>
			);
		}
		return String(value || 'N/A');
	};

	useEffect(() => {
		if (!intakeMetadata) {
			setMetadata(null);
			return;
		}
		try {
			const parsed = JSON.parse(intakeMetadata);
			setMetadata(parsed);
		} catch {
			setMetadata(null);
		}
	}, [intakeMetadata]);

	const renderFormResponse = (data: any, index?: number) => {
		const title = index !== undefined ? `Response ${index + 1}` : 'Intake Form Response';

		return (
			<Card key={index} className="mb-4" size="small">
				<div className="flex align-middle gap-2 mb-4">
					<FileText className="w-5 h-5" />
					<h4 className="text-base font-medium leading-5">{title}</h4>
				</div>

				<Descriptions column={1} size="small" bordered>
					{Object.entries(data).map(([key, value]) => (
						<Descriptions.Item
							key={key}
							label={
								<span className="font-medium capitalize">
									{key.replace(/([A-Z])/g, ' $1').replace(/^./, (str) => str.toUpperCase())}
								</span>
							}
						>
							{renderValue(value)}
						</Descriptions.Item>
					))}
				</Descriptions>
				<div className="mt-4 pt-4 border-t">
					<h5 className="text-sm font-semibold mb-3 text-gray-700">Response Actions</h5>
					<div className="space-y-3">
						{!isNewStatusSystemEnabled ? (
							<div className="flex items-center gap-2">
								<Checkbox
									checked={!!metadata?.completed_at}
									onChange={(e) => handleCompletionChange(e.target.checked)}
									disabled={updateResponseMutation.isPending}
								>
									Mark as Completed
								</Checkbox>

								{updateResponseMutation.isPending ? (
									<Spin size="small" />
								) : (
									metadata?.completed_at && (
										<Tag color="green">
											Completed on {new Date(metadata.completed_at).toLocaleDateString()}
										</Tag>
									)
								)}
							</div>
						) : (
							<div className="flex items-center gap-2">
								<span className="text-sm text-gray-600 mr-2">Status:</span>
								<Select
									value={currentStatus}
									onChange={handleStatusChange}
									style={{ width: 150 }}
									disabled={updateResponseMutation.isPending}
								>
									<Option value="OPEN">Open</Option>
									<Option value="IN_PROGRESS">In Progress</Option>
									<Option value="COMPLETED">Completed</Option>
								</Select>
								{updateResponseMutation.isPending ? (
									<Spin size="small" />
								) : (
									<>
										{currentStatus === 'IN_PROGRESS' && metadata?.in_progress_at && (
											<Tag color="gold">
												In Progress since {new Date(metadata.in_progress_at).toLocaleDateString()}
											</Tag>
										)}
										{currentStatus === 'COMPLETED' && metadata?.completed_at && (
											<Tag color="green">
												Completed on {new Date(metadata.completed_at).toLocaleDateString()}
											</Tag>
										)}
									</>
								)}
							</div>
						)}
						<div>
							{metadata?.note ? (
								<div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
									<p className="text-sm font-semibold text-gray-700 mb-1">Note:</p>
									<div className="flex items-start  gap-1">
										<p className="text-sm text-gray-800 whitespace-pre-wrap">{metadata.note}</p>
										<Tooltip title="Edit Note">
											<Button
												type="text"
												shape="circle"
												size="small"
												icon={<EditOutlined />}
												onClick={handleOpenNoteModal}
												className="flex-shrink-0"
											/>
										</Tooltip>
									</div>
								</div>
							) : (
								<Button size="small" onClick={handleOpenNoteModal}>
									Add Note
								</Button>
							)}
						</div>
					</div>
				</div>
			</Card>
		);
	};

	if (error) {
		return (
			<div className="text-center py-8">
				<div className="text-4xl text-red-400 mb-4">⚠️</div>
				<h3 className="text-lg font-medium text-gray-900 mb-2">Error Loading Intake Response</h3>
				<p className="text-red-500 mb-4">{error}</p>
				<div className="text-xs bg-red-50 p-3 rounded border border-red-200">
					<pre>{intakeResponses}</pre>
				</div>
			</div>
		);
	}

	if (!parsedData) {
		return (
			<div className="text-center py-8">
				<div className="text-4xl text-gray-400 mb-4">📝</div>
				<h3 className="text-lg font-medium text-gray-900 mb-2">Loading Intake Response...</h3>
			</div>
		);
	}

	return (
		<div className="space-y-4">
			{/* Response Data */}
			{Array.isArray(parsedData) ? (
				parsedData.length > 0 ? (
					parsedData.map((response, index) => renderFormResponse(response, index))
				) : (
					<div className="text-center py-8">
						<div className="text-4xl text-gray-400 mb-4">📝</div>
						<p className="text-gray-500">No intake responses found</p>
					</div>
				)
			) : typeof parsedData === 'object' && parsedData !== null ? (
				renderFormResponse(parsedData)
			) : (
				<div className="text-center py-8">
					<div className="text-4xl text-gray-400 mb-4">📝</div>
					<p className="text-gray-500">Invalid intake response format</p>
					<div className="text-xs bg-gray-50 p-3 rounded border mt-4">
						<pre>{JSON.stringify(parsedData, null, 2)}</pre>
					</div>
				</div>
			)}
			<NotesModal />
		</div>
	);
};

export default IntakeResponseTab;
